import { UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Minus, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import type { CreateTripInput } from "@/lib/validations";

interface PassengersAccordionProps {
  watch: UseFormWatch<CreateTripInput>;
  setValue: UseFormSetValue<CreateTripInput>;
}

export default function PassengersAccordion({ watch, setValue }: PassengersAccordionProps) {
  const passengers = watch("passengers") ?? 1;

  function set(next: number) {
    setValue("passengers", Math.min(9, Math.max(1, next)), { shouldDirty: true });
  }

  return (
    <AccordionItem value="passengers">
      <AccordionTrigger>
        <span className="flex items-center gap-2">
          Passengers
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
            {passengers}
          </span>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="flex items-center gap-4">
          <Label htmlFor="passengers-count" className="text-sm font-normal">
            Number of travelers
          </Label>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => set(passengers - 1)}
              disabled={passengers <= 1}
              aria-label="Remove a passenger"
            >
              <Minus className="h-4 w-4" aria-hidden="true" />
            </Button>
            <span
              id="passengers-count"
              className="min-w-[2rem] text-center text-lg font-semibold"
              aria-live="polite"
            >
              {passengers}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => set(passengers + 1)}
              disabled={passengers >= 9}
              aria-label="Add a passenger"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
